import { useEffect, useState, useRef, RefObject } from "react";

/**
 * useOnScreen hook that makes use of Intersection observer to see if an element is rendered on the page
 * Useful if you are waiting to make sure an element is rendered to the screen in the correct paint position
 * before targeting - eg for the map tour
 * How to use:
 * const elementRef = useRef(null);
 * let isOnScreen = useOnScreen(elementRef);
 * <ElemToTarget ref={elementRef} />
 * @param ref the element that you want to reference to check if it is rendered on screen
 * @returns {isOnScreen: boolean}
 */

export default function useOnScreen(ref: RefObject<HTMLElement>) {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const [isOnScreen, setIsOnScreen] = useState(false);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(([entry]) => setIsOnScreen(entry.isIntersecting));
    }, []);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        observerRef.current?.observe(ref.current!);

        return () => {
            observerRef.current?.disconnect();
        };
    }, [ref]);

    return isOnScreen;
}
